import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Logo } from "@/components/Logo";
import api from "@/lib/api";
import { Mail } from "lucide-react";
import { toast } from "sonner";
import { usePageMeta } from "@/lib/usePageMeta";

export default function ForgotPassword() {
  usePageMeta("Reset your password · Suffle Online", "Reset your Suffle Online password securely.");
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email, app_origin: window.location.origin });
      setSent(true);
    } catch (err) { toast.error("Something went wrong. Please try again."); }
    finally { setLoading(false); }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 p-6">
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md rounded-2xl bg-white p-8 shadow-xl" data-testid="forgot-page">
        <Logo variant="lg" />
        {sent ? (
          <div className="mt-6 text-center" data-testid="forgot-sent">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-emerald-50 text-emerald-500"><Mail className="h-7 w-7" /></div>
            <h1 className="mt-4 font-brand text-2xl font-bold text-slate-800">Check your inbox</h1>
            <p className="mt-2 text-sm text-slate-500">If an account exists for <span className="font-semibold text-slate-700">{email}</span>, we've sent a password reset link. It expires in 1 hour.</p>
            <Link to="/login" className="mt-6 inline-block w-full rounded-xl brand-gradient-bg py-3 font-semibold text-white">Back to Login</Link>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-6">
            <h1 className="font-brand text-2xl font-bold text-slate-800">Forgot password?</h1>
            <p className="mt-1 text-sm text-slate-500">Enter your email and we'll send you a reset link.</p>
            <label className="mt-5 block text-sm font-medium text-slate-600">Email</label>
            <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="forgot-email"
              className="mt-1 w-full rounded-xl border border-slate-200 px-4 py-3 outline-none focus:border-blue-400" placeholder="you@example.com" />
            <button type="submit" disabled={loading} data-testid="forgot-submit"
              className="mt-6 w-full rounded-xl brand-gradient-bg py-3 font-semibold text-white disabled:opacity-50">
              {loading ? "Sending..." : "Send reset link"}
            </button>
            <Link to="/login" className="mt-4 block text-center text-sm text-slate-500 hover:text-slate-700">Back to Login</Link>
          </form>
        )}
      </motion.div>
    </div>
  );
}
